import type { ErrorCode } from './protocol.ts'
import {
  ACTION_CARD_CREATE,
  ACTION_LIST,
  ACTION_PAYMENT_CREATE,
  ACTION_SELECT,
  MAX_PAYMENT_ATTEMPTS,
  type CardDraft,
  type MethodResult,
  type PaymentDecline,
  type PaymentMethodSummary,
  type PaymentQuote,
  type PaymentRecord,
  type PaymentResult,
  type SessionConfig,
  type SuccessResult,
  type TerminalError
} from './types.ts'

export type Status =
  | 'boot'
  | 'ready_posted'
  | 'exchanging'
  | 'method_list'
  | 'card_entry'
  | 'submitting'
  | 'quoting'
  | 'confirm'
  | 'paying'
  | 'recovering'
  | 'declined'
  | 'payment_unknown'
  | 'success'
  | 'error'
  | 'closed'

export interface PaymentState {
  paymentMethodId: number
  quote: PaymentQuote | null
  idempotencyKey: string | null
  submissions: number
  decline: PaymentDecline | null
}

export interface TerminalState {
  status: Status
  generation: number
  session: SessionConfig | null
  methods: PaymentMethodSummary[]
  draft: CardDraft | null
  result: SuccessResult | null
  error: TerminalError | null
  payment: PaymentState | null
  attemptsUsed: number
}

export type TerminalEvent =
  | { type: 'READY_POSTED' }
  | { type: 'PAYMENT_INTERRUPTED'; message: string }
  | { type: 'INIT'; generation: number }
  | {
      type: 'SESSION_LOADED'
      generation: number
      session: SessionConfig
      methods: PaymentMethodSummary[]
    }
  | { type: 'OPEN_CARD_ENTRY' }
  | { type: 'CARD_TOKENIZED'; generation: number; draft: CardDraft }
  | { type: 'BACK' }
  | { type: 'METHOD_CREATED'; generation: number; method: PaymentMethodSummary }
  | { type: 'METHOD_SELECTED'; paymentMethodId: number }
  | {
      type: 'QUOTED'
      generation: number
      paymentMethodId: number
      quote: PaymentQuote
    }
  | { type: 'PAY'; idempotencyKey: string }
  | {
      type: 'PAYMENT_SUCCEEDED'
      generation: number
      attemptId: number
      payment: PaymentRecord
    }
  | {
      type: 'PAYMENT_DECLINED'
      generation: number
      attemptId: number
      decline: PaymentDecline
    }
  | { type: 'PAYMENT_UNKNOWN'; generation: number; attemptId: number }
  | { type: 'RESUBMIT'; generation: number; attemptId: number }
  | {
      type: 'PAYMENT_LOST'
      generation: number
      attemptId: number
      message: string
    }
  | { type: 'CHANGE_CARD' }
  | { type: 'FAILED'; generation: number; code: ErrorCode; message: string }
  | { type: 'RETRY' }
  | { type: 'CLOSE' }

export const initialState: TerminalState = {
  status: 'boot',
  generation: 0,
  session: null,
  methods: [],
  draft: null,
  result: null,
  error: null,
  payment: null,
  attemptsUsed: 0
}

/** Submits of one attempt, with one idempotency key, before the terminal only polls. */
export const MAX_SUBMISSIONS = 3

// A charge may be in flight. Close, init and renewal must not reset these screens.
export const PAYMENT_IN_FLIGHT: ReadonlySet<Status> = new Set<Status>([
  'paying',
  'recovering'
])

const RETRYABLE: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'processor_decline',
  'duplicate_card',
  'validation',
  'rate_limited',
  'unavailable'
])

function allows(state: TerminalState, action: string): boolean {
  return state.session?.actions.includes(action) ?? false
}

function result(
  state: TerminalState,
  method: PaymentMethodSummary,
  action: MethodResult['action']
): MethodResult {
  return {
    action,
    actionId: state.session?.actionId ?? '',
    paymentMethodId: method.paymentMethodId,
    paymentType: method.paymentType,
    brand: method.brand,
    last4: method.last4,
    expMonth: method.expMonth,
    expYear: method.expYear,
    status: action === ACTION_SELECT ? 'selected' : 'created'
  }
}

function paymentResult(
  state: TerminalState,
  payment: PaymentRecord
): PaymentResult {
  return {
    action: ACTION_PAYMENT_CREATE,
    actionId: state.session?.actionId ?? '',
    paymentId: payment.paymentId,
    status: payment.status,
    amount: payment.amount,
    fee: payment.fee,
    total: payment.total,
    currency: payment.currency,
    brand: payment.method.brand,
    last4: payment.method.last4
  }
}

/**
 * Tells whether the session takes a payment after a card is chosen.
 * @param state - The current state.
 * @returns True when the session allows `payment.create`.
 */
export function takesPayment(state: TerminalState): boolean {
  return allows(state, ACTION_PAYMENT_CREATE)
}

function quoting(
  state: TerminalState,
  method: PaymentMethodSummary
): TerminalState {
  return {
    ...state,
    status: 'quoting',
    draft: null,
    error: null,
    payment: {
      paymentMethodId: method.paymentMethodId,
      quote: null,
      idempotencyKey: null,
      submissions: 0,
      decline: null
    }
  }
}

function forAttempt(state: TerminalState, attemptId: number): boolean {
  return (
    PAYMENT_IN_FLIGHT.has(state.status) &&
    state.payment?.quote?.attemptId === attemptId
  )
}

function pickAnotherCard(state: TerminalState): TerminalState {
  const status = allows(state, ACTION_LIST) ? 'method_list' : 'card_entry'
  return { ...state, status, payment: null, error: null, draft: null }
}

function afterSession(state: TerminalState): TerminalState {
  if (allows(state, ACTION_LIST)) return { ...state, status: 'method_list' }
  if (allows(state, ACTION_CARD_CREATE)) {
    return { ...state, status: 'card_entry' }
  }
  return {
    ...state,
    status: 'error',
    error: {
      code: 'action_forbidden',
      message: 'This session allows no action this terminal supports.'
    }
  }
}

/**
 * Applies one event to the terminal state, dropping events from an earlier generation.
 * @param state - The current state.
 * @param event - The event.
 * @returns The next state, or the same object when the event does not apply.
 */
export function reduce(
  state: TerminalState,
  event: TerminalEvent
): TerminalState {
  if ('generation' in event && event.type !== 'INIT') {
    if (event.generation !== state.generation) return state
  }
  if (state.status === 'closed' && event.type !== 'INIT') return state
  if (
    event.type === 'INIT' &&
    ((takesPayment(state) && state.status === 'submitting') ||
      state.error?.code === 'payment_unknown' ||
      state.result?.action === ACTION_PAYMENT_CREATE)
  )
    return state
  if (
    PAYMENT_IN_FLIGHT.has(state.status) &&
    (event.type === 'INIT' || event.type === 'CLOSE')
  ) {
    return state
  }

  switch (event.type) {
    case 'PAYMENT_INTERRUPTED':
      if (state.status !== 'boot' && state.status !== 'ready_posted')
        return state
      return {
        ...state,
        status: 'payment_unknown',
        error: {
          code: 'payment_unknown',
          message: event.message
        }
      }
    case 'READY_POSTED':
      return state.status === 'boot'
        ? { ...state, status: 'ready_posted' }
        : state
    case 'INIT':
      if (event.generation <= state.generation) return state
      return {
        ...initialState,
        status: 'exchanging',
        generation: event.generation
      }
    case 'SESSION_LOADED':
      if (state.status !== 'exchanging') return state
      return afterSession({
        ...state,
        session: event.session,
        methods: event.methods
      })
    case 'OPEN_CARD_ENTRY':
      if (state.status !== 'method_list') return state
      if (!allows(state, ACTION_CARD_CREATE)) return state
      return { ...state, status: 'card_entry' }
    case 'CARD_TOKENIZED':
      if (state.status !== 'card_entry') return state
      return { ...state, status: 'submitting', draft: event.draft }
    case 'BACK':
      if (state.status === 'card_entry' && allows(state, ACTION_LIST)) {
        return { ...state, status: 'method_list' }
      }
      if (
        state.status === 'error' &&
        state.error?.code === 'duplicate_card' &&
        allows(state, ACTION_LIST)
      ) {
        return { ...state, status: 'method_list', error: null, draft: null }
      }
      return state
    case 'METHOD_CREATED':
      if (state.status !== 'submitting') return state
      if (takesPayment(state)) {
        return quoting(
          { ...state, methods: [...state.methods, event.method] },
          event.method
        )
      }
      return {
        ...state,
        status: 'success',
        draft: null,
        methods: [...state.methods, event.method],
        result: result(state, event.method, ACTION_CARD_CREATE)
      }
    case 'METHOD_SELECTED': {
      if (state.status !== 'method_list') return state
      const method = state.methods.find(
        m => m.paymentMethodId === event.paymentMethodId
      )
      if (!method) return state
      if (takesPayment(state)) {
        return method.paymentType === 'card' ? quoting(state, method) : state
      }
      return {
        ...state,
        status: 'success',
        result: result(state, method, ACTION_SELECT)
      }
    }
    case 'QUOTED':
      if (state.status !== 'quoting' || !state.payment) return state
      if (state.payment.paymentMethodId !== event.paymentMethodId) return state
      return {
        ...state,
        status: 'confirm',
        payment: { ...state.payment, quote: event.quote }
      }
    case 'PAY':
      if (state.status !== 'confirm' || !state.payment?.quote) return state
      if (state.attemptsUsed >= MAX_PAYMENT_ATTEMPTS) return state
      return {
        ...state,
        status: 'paying',
        attemptsUsed: state.attemptsUsed + 1,
        payment: {
          ...state.payment,
          idempotencyKey: event.idempotencyKey,
          submissions: 1
        }
      }
    case 'PAYMENT_SUCCEEDED':
      if (!forAttempt(state, event.attemptId)) return state
      return {
        ...state,
        status: 'success',
        result: paymentResult(state, event.payment)
      }
    case 'PAYMENT_DECLINED': {
      if (!forAttempt(state, event.attemptId) || !state.payment) return state
      const attemptsRemaining = Math.min(
        event.decline.attemptsRemaining,
        MAX_PAYMENT_ATTEMPTS - state.attemptsUsed
      )
      return {
        ...state,
        status: 'declined',
        error: { code: 'processor_decline', message: event.decline.reason },
        payment: {
          ...state.payment,
          decline: { reason: event.decline.reason, attemptsRemaining }
        }
      }
    }
    case 'PAYMENT_UNKNOWN':
      if (state.status !== 'paying' || !forAttempt(state, event.attemptId)) {
        return state
      }
      return { ...state, status: 'recovering' }
    case 'RESUBMIT':
      if (
        state.status !== 'recovering' ||
        !forAttempt(state, event.attemptId)
      ) {
        return state
      }
      if (!state.payment || state.payment.submissions >= MAX_SUBMISSIONS) {
        return state
      }
      return {
        ...state,
        status: 'paying',
        payment: {
          ...state.payment,
          submissions: state.payment.submissions + 1
        }
      }
    case 'PAYMENT_LOST':
      if (!forAttempt(state, event.attemptId)) return state
      return {
        ...state,
        status: 'payment_unknown',
        error: { code: 'payment_unknown', message: event.message }
      }
    case 'CHANGE_CARD':
      if (state.status === 'confirm') return pickAnotherCard(state)
      if (
        state.status === 'declined' &&
        (state.payment?.decline?.attemptsRemaining ?? 0) > 0
      ) {
        return pickAnotherCard(state)
      }
      return state
    case 'FAILED':
      if (
        state.status === 'recovering' ||
        (state.status === 'paying' && (state.payment?.submissions ?? 0) > 1)
      )
        return state
      if (state.status === 'payment_unknown') return state
      if (state.result?.action === ACTION_PAYMENT_CREATE) return state
      return {
        ...state,
        status: 'error',
        attemptsUsed:
          state.status === 'paying' && state.payment?.submissions === 1
            ? Math.max(0, state.attemptsUsed - 1)
            : state.attemptsUsed,
        error: { code: event.code, message: event.message }
      }
    case 'RETRY':
      if (state.status !== 'error' || !state.error) return state
      if (!RETRYABLE.has(state.error.code)) return state
      if (state.payment) return pickAnotherCard(state)
      if (!allows(state, ACTION_CARD_CREATE)) return state
      return { ...state, status: 'card_entry', error: null, draft: null }
    case 'CLOSE':
      return { ...state, status: 'closed' }
    default:
      return state
  }
}
